/**
 * What each nav destination is actually called once you land on it.
 *
 * Menu labels and on-page headings drift apart: the menu says "Studio
 * Settings", the page says "Configure your studio"; the menu says "LP
 * Enrollments", the page says "Learning path enrollments". People search for
 * whichever one they last read, so search has to know both.
 *
 * `heading` and `description` are transcribed from the page's own H1 and
 * subheading — they are not invented copy, and they should be re-transcribed
 * (not paraphrased) whenever a page's header text changes. `aliases` cover
 * renames and the shorthand a page is known by, so old vocabulary keeps working
 * after a rename.
 *
 * Two tests keep this honest: `navigationPageIndex.test.js` asserts every key
 * still resolves to a live route, and `navigationPageHeadings.test.js` asserts
 * every heading and description here is still rendered by that route. So record
 * only what those can check — a heading that just repeats the menu label, or one
 * assembled at runtime, should be an alias instead.
 *
 * See docs/navigation-search.md for what those guards cannot catch (chiefly
 * missing aliases, which only real search traffic reveals) and the options for
 * closing that gap.
 */

import { isOpaqueIdSegment } from "@/lib/navigation/page-search-match";

/**
 * Collapse a live href into a stable directory key: drop the locale prefix,
 * query string, and trailing slash, and replace the workspace id with a
 * placeholder so per-studio URLs share one entry.
 *
 * The workspace segment is recognised by the shape of an id, not by listing the
 * sibling routes it isn't. A denylist of static segments (`admin`, `reports`, …)
 * would go quietly wrong the day someone adds `/workspaces/settings`: that URL
 * would normalise to a studio-id key and lose its directory entry with no
 * failing test to show for it.
 */
export function normalizeRouteKey(href) {
  if (typeof href !== "string" || !href.trim()) return "";

  const [pathWithHash] = href.split("?", 1);
  const [path, hash = ""] = pathWithHash.split("#", 2);

  const normalizedPath = path
    .replace(/^\/(es|fr-CA)(?=\/|$)/, "")
    .replace(/^\/workspaces\/([^/]+)/, (match, segment) =>
      isOpaqueIdSegment(segment) ? "/workspaces/:workspaceId" : match,
    )
    .replace(/\/+$/, "")
    .toLowerCase();

  return hash ? `${normalizedPath}#${hash.toLowerCase()}` : normalizedPath;
}

const PAGE_DIRECTORY = {
  // ---------------------------------------------------------------- Learn
  "/playbook": {
    aliases: ["company overview", "positioning", "glossary", "lessons"],
  },
  "/playbook#our-content-faq": { aliases: ["faq", "faqs", "questions"] },
  "/playbook#fbiv": { aliases: ["fbiv", "features benefits impact value"] },
  "/playbook#messaging": { aliases: ["pitch", "pitches", "value message"] },
  "/playbook#cold-call": { aliases: ["cold call", "outbound", "inbound"] },
  "/dashboard/learning-paths": {
    heading: "Learning Paths",
    aliases: ["lp", "courses", "curriculum"],
  },
  "/training": {
    heading: "Your Training",
    aliases: ["my training", "to do", "assigned work"],
  },
  "/stories": { heading: "Stories" },

  // ------------------------------------------------------------- Practice
  "/simulator": {
    aliases: ["simulator", "roleplay", "scenarios", "practice scenarios"],
  },
  "/simulator/personas": {
    heading: "Personas Playbook",
    aliases: ["buyer personas", "customer personas"],
  },
  "/simulator/seller-personas": {
    heading: "Other Personas",
    aliases: ["seller personas", "rep personas"],
  },
  "/simulator/on-the-fly": {
    heading: "On-the-Fly Practice",
    aliases: ["ad hoc practice", "quick practice"],
  },

  // ------------------------------------------------------------ Execution
  "/dem-cog/reports": {
    heading: "Deal Diagnostic Readouts",
    aliases: ["deal clinic", "cog", "cog report", "deal review"],
  },
  "/dem-cog/dems": {
    heading: "Discovery Expert Models",
    aliases: ["dem", "dems", "expert models"],
  },
  "/dashboard/conversation-analyzer": {
    aliases: ["transcript analysis", "call analysis", "analyze a call"],
  },
  "/workspaces/scripts": {
    heading: "Script Generator",
    aliases: ["email scripts", "talking scripts"],
  },

  // ------------------------------------------------------------- Results
  "/dashboard": {
    heading: "My Results",
    description: "Track your results and assignments",
    aliases: ["my scores", "my progress", "home dashboard"],
  },
  "/leaderboard": { aliases: ["rankings", "standings"] },
  "/dashboard/assignments": {
    heading: "Assignments",
    description:
      "Practice scenarios assigned to you. Tests and learning paths are tracked separately under Tests and Learning Paths.",
    aliases: ["my assignments", "assigned practice", "pending work"],
  },
  "/dashboard/tests": {
    heading: "Tests",
    description: "Complete your assigned knowledge assessments.",
    aliases: ["quizzes", "knowledge checks", "assessments"],
  },
  "/dashboard/okrs": {
    heading: "OKRs",
    aliases: ["objectives", "key results", "goals"],
  },
  "/dashboard/badges": { heading: "Badges", aliases: ["achievements"] },
  "/dashboard/feedback-analysis": {
    aliases: ["my coaching", "ai coaching"],
  },
  "/dashboard/feedback-comments": {
    heading: "Feedback",
    aliases: ["comments", "my feedback"],
  },
  "/dashboard/persona-chats": {
    heading: "Persona chats",
    aliases: ["chat history", "persona conversations"],
  },
  "/chats": { aliases: ["conversations", "past chats"] },

  // ------------------------------------------------------------- Reports
  "/workspaces/reports": {
    heading: "All results",
    aliases: ["all reports", "team results", "reporting"],
  },
  "/workspaces/reports/manager-dashboard": {
    heading: "Manager Dashboard",
    aliases: ["team overview", "manager view"],
  },
  "/workspaces/reports/execution": {
    heading: "Execution module usage",
    aliases: ["module usage", "adoption"],
  },
  "/workspaces/reports/transcript-results": {
    aliases: ["transcripts", "call analysis"],
  },
  // These three share one [period] route whose heading is assembled from the
  // period label at runtime, so there is no fixed heading to record.
  "/workspaces/reports/engagement/quarterly": {
    aliases: ["by quarter", "quarterly report", "quarterly engagement"],
  },
  "/workspaces/reports/engagement/monthly": {
    aliases: ["by month", "monthly report", "monthly engagement"],
  },
  "/workspaces/reports/engagement/weekly": {
    aliases: ["by week", "weekly report", "weekly engagement"],
  },
  "/workspaces/reports/enrollments": {
    heading: "Learning path enrollments",
    aliases: [
      "lp enrollments",
      "enrolled learners",
      "time tracking",
      "time spent",
      "hours",
    ],
  },
  "/workspaces/reports/overdue-items": {
    heading: "Overdue items",
    aliases: ["late work", "past due"],
  },
  "/workspaces/reports/tests/multiple-choice": {
    heading: "Multiple choice tests",
    description: "Review how your team performed on multiple choice tests.",
  },
  "/workspaces/reports/tests/faq": {
    heading: "Q&A tests",
    description: "Review how your team performed on Q&A tests.",
    aliases: ["faq tests"],
  },
  "/workspaces/reports/tests/glossary": {
    heading: "Glossary tests",
    description: "Review how your team performed on glossary tests.",
  },
  "/workspaces/reports/recordings": {
    heading: "Recordings library",
    aliases: ["call recordings", "audio"],
  },
  "/workspaces/reports/coaching-report": {
    heading: "Coaching report",
    aliases: ["per rep coaching"],
  },
  "/workspaces/reports/team-coaching": {
    heading: "Team Coaching Report",
    description: "Aggregate performance insights across your team",
  },
  "/workspaces/reports/feedback-comments": {
    heading: "Studio feedback",
    aliases: ["feedback comments"],
  },
  "/workspaces/reports/practice-sessions-export": {
    heading: "Practice sessions export",
    aliases: ["export sessions", "csv export", "download sessions"],
  },

  // --------------------------------------------------------------- Admin
  "/workspaces/:workspaceid/edit": {
    heading: "Configure your studio",
    description:
      "Customize your studio's profile, language, notifications, and connections.",
    aliases: [
      "studio settings",
      "workspace settings",
      "configure studio",
      "studio profile",
      "notifications",
      "sales language",
      "connections",
      "integrations",
      "training modules",
      "plugins",
    ],
  },
  "/workspaces/:workspaceid/positioning": {
    heading: "Positioning",
    aliases: ["value proposition", "messaging foundation"],
  },
  "/workspaces/admin/data/content": {
    heading: "Content",
    aliases: ["resources", "documents", "decks", "uploads"],
  },
  // The per-group pages under /glossary/groups/[groupId] cannot have their own
  // entries: a directory key has to resolve to a live nav destination (see
  // navigationPageIndex.test.js) and a group page's URL is only known once a
  // group exists. Their vocabulary rides along here instead, on the library
  // that lists them.
  "/workspaces/admin/glossary": {
    heading: "Glossary",
    aliases: ["terms", "definitions", "glossary groups", "term groups"],
  },
  "/workspaces/admin/faqs": {
    heading: "Q&As",
    aliases: ["faq", "faqs", "questions and answers"],
  },
  "/workspaces/admin/fbiv": {
    heading: "Capability Maps",
    aliases: ["fbiv", "features benefits impact value"],
  },
  "/workspaces/admin/people": {
    heading: "Studio members",
    aliases: ["users", "team members", "seats", "invite"],
  },
  "/workspaces/admin/people/communities": {
    heading: "Communities",
    description: "Create and manage communities to organize your members.",
    aliases: ["groups", "teams"],
  },
  "/workspaces/admin/role-plays": {
    heading: "Assignments",
    description: "Create and manage assignments for your team.",
    aliases: ["roleplays", "assign practice"],
  },
  "/workspaces/admin/learning-paths": {
    heading: "Learning Path Management",
    description: "Build and assign multi-step learning paths for your team.",
    aliases: ["lp builder", "build a course"],
  },
  "/workspaces/admin/tests": {
    heading: "Test management",
    description: "Create multiple-choice tests and assign them to your team.",
    aliases: ["quizzes", "knowledge checks"],
  },
  "/workspaces/admin/okrs": {
    heading: "Objectives and key results",
    description: "Set objectives and track key results across your team.",
    aliases: ["goals"],
  },
  "/workspaces/admin/badges": {
    heading: "Badges",
    description: "Recognize milestones and achievements across your team.",
  },
  "/workspaces/admin/analytics": {
    heading: "Analytics",
    aliases: ["usage", "activity breakdown"],
  },
  "/workspaces/admin/data/personas": {
    heading: "Buyer personas",
    description: "Manage and organize your buyer personas.",
  },
  "/workspaces/admin/data/rubrics": {
    heading: "Studio rubrics",
    aliases: ["scorecards", "grading", "scoring"],
  },
  "/workspaces/admin/data/discovery": {
    heading: "Discovery",
    description: "Manage and organize your discovery tracks.",
  },
  "/workspaces/admin/data/pitch": {
    heading: "Messaging / pitches",
    description: "Manage and organize your sales pitches and messaging.",
  },
  "/workspaces/admin/data/cold-call": {
    heading: "Outbound & Inbounds",
    description: "Manage your Outbound & Inbound scenarios.",
  },
  "/workspaces/admin/data/objections": {
    heading: "Objection handling",
    description: "Manage and organize your customer objections.",
  },
  "/workspaces/admin/data/plays": {
    heading: "Play scenarios",
    description: "Manage and organize your sales play scenarios.",
  },
  "/workspaces/admin/data/management-scenarios": {
    heading: "Management scenarios",
    description: "Manage your management practice scenarios.",
  },
  "/workspaces/admin/data/seller-personas": {
    heading: "Other personas",
    description: "Manage seller and other personas for your studio.",
  },
  "/workspaces/broadcast": {
    heading: "Broadcast Messages",
    aliases: ["announcement", "message everyone", "email the team"],
  },
  "/help/admin": {
    heading: "Admin Help Center",
    aliases: ["documentation", "how to"],
  },

  // ------------------------------------------------------------- Off-menu
  "/settings": {
    heading: "Account Settings",
    aliases: [
      "my profile",
      "password",
      "timezone",
      "email notifications",
      "two factor",
    ],
  },
  "/help": {
    heading: "How can we help?",
    aliases: ["support", "documentation", "contact us"],
  },
};

/**
 * Destinations people search for that no top-nav menu lists — reachable from
 * the avatar menu or a footer link, and previously unfindable through search.
 *
 * `requiresWorkspace` mirrors the gate on the control that links to them, the
 * same way every other entry in `navigation-pages.js` carries `display:
 * hasWorkspace`. Note that it does not currently decide anything:
 * `searchNavigationPages` needs a studio to resolve labels and module gating at
 * all, so it returns nothing before the flag is read. It records what the page
 * needs, so the answer is already right if that entry condition ever widens.
 */
export const OFF_MENU_SEARCH_PAGES = [
  {
    href: "/settings",
    label: "Account Settings",
    section: "Account",
    requiresWorkspace: true,
  },
  {
    href: "/help",
    label: "Help Center",
    section: "Support",
    requiresWorkspace: false,
  },
];

export function getPageDirectoryEntry(href) {
  return PAGE_DIRECTORY[normalizeRouteKey(href)] || null;
}

/** Every route this directory describes — used to test for stale entries. */
export function getPageDirectoryKeys() {
  return Object.keys(PAGE_DIRECTORY);
}

/** Route key → entry pairs, for tests that verify entries against page source. */
export function getPageDirectoryEntries() {
  return Object.entries(PAGE_DIRECTORY);
}
